/**
 * "This is a beta" on the site chrome.
 *
 * Drawn once, in the root layout, so it covers every page the shell wraps — the landing page,
 * sign-in, register, the profile, the leaderboard and the operator console — without any of
 * them knowing it exists.
 *
 * Two rules shape it:
 *
 * - **It is not on `/play`.** The arena is the product, and a strip across the top of it would
 *   be play area spent on a sentence a player has already read on the way in. The game says the
 *   nothing at all: the shell is hidden during a live match, and the arena used to draw a
 *   badge of its own in the corner of its HUD until a player asked for that corner back.
 * - **It does not reflow anything.** One line at every width — the copy itself is swapped at
 *   the breakpoint rather than allowed to wrap — and it sits in the sticky stack under the
 *   header, so it neither overlaps the nav above it nor moves the content below it.
 * - **It is opaque.** It is sticky, so the page scrolls *under* it, and the 10% accent tint it
 *   used to wear let that text through — the strip and a paragraph passing behind it read as one
 *   line of overlapping words. `BETA_SURFACE_CLASS` is the fix and is shared with the badge; the
 *   inner label keeps its own outline instead of a second translucent fill.
 *
 * Where it sticks is `--chomp-header-height` (`app.css`), not a repeated `top-14`. The header is
 * `min-h-14` PLUS whatever it spends on the top safe-area inset, so on any device with a status
 * bar over the page a literal 3.5rem parked this strip *underneath* the header and hid it — the
 * banner's contribution to "the header is off screen".
 */

import { useRouterState } from '@tanstack/react-router';
import {
  BETA_ENABLED,
  BETA_LABEL,
  BETA_NOTICE,
  BETA_NOTICE_SHORT,
  BETA_SURFACE_CLASS,
} from '@/lib/beta';

export function BetaBanner() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  if (!BETA_ENABLED) return null;
  // The game draws its own; see the note above.
  if (pathname.startsWith('/play')) return null;

  return (
    <div
      data-testid="beta-banner"
      // `.safe-header-offset` is the header's own height in `__root.tsx`, inset included: the two
      // are a stack, not a layer. `.safe-gutter-x` is the side insets, which the header spends on
      // its own row and this one has to spend again — an inset is a viewport constant, so every
      // full-width strip owes it separately.
      className={`safe-header-offset safe-gutter-x sticky z-30 flex shrink-0 items-center justify-center gap-2 border-b border-accent/30 py-1 text-center text-[11px] leading-tight text-accent sm:text-xs ${BETA_SURFACE_CLASS}`}
    >
      <span className="shrink-0 rounded border border-accent/40 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider">
        {BETA_LABEL}
      </span>
      <span className="sm:hidden">{BETA_NOTICE_SHORT}</span>
      <span className="hidden sm:inline">{BETA_NOTICE}</span>
    </div>
  );
}
